<template>
  <div>
    <van-empty v-if="noid" description="参数错误" />
    <div v-else class="enterprise-info">
      <!-- 企业头部信息 -->
      <div class="header">
        <div class="header-content">
          <h1 class="enterprise-name">{{ enterpriseName }}</h1>
          <p class="enterprise-code" v-show="idNumber">
            企业税号：{{ idNumber }}
          </p>
          <p class="enterprise-address" v-show="regLocation">
            注册地址：{{ regLocation }}
          </p>
        </div>
      </div>

      <!-- 主要内容区 -->
      <div class="content">
        <!-- 税务信用信息 -->
        <van-collapse v-model="activeNames" class="info-section">
          <van-collapse-item name="tax" title="税务信用等级">
            <div v-if="taxCredits.length > 0" class="tax-credit-list">
              <van-cell
                v-for="(credit, index) in taxCredits"
                :key="index"
                :title="`${credit.year}年 信用等级: ${credit.grade}`"
                :value="credit.evalDepartment"
                is-link
                @click="showTaxDetail(credit)"
              ></van-cell>
            </div>
            <div v-else class="empty-info">
              <van-empty description="暂无税务信用信息" />
            </div>
          </van-collapse-item>
        </van-collapse>

        <!-- 税务违规信息 -->
        <van-collapse v-model="activeNames" class="info-section">
          <van-collapse-item name="violations" title="税务违规记录">
            <div v-if="taxContraventions.length > 0" class="violation-list">
              <van-cell
                v-for="(violation, index) in taxContraventions"
                :key="index"
                :title="violation.title"
                :value="violation.date"
              ></van-cell>
            </div>
            <div v-else class="empty-info">
              <van-empty description="暂无税务违规记录" />
              <p class="empty-hint">企业税务记录良好，无任何违规记录</p>
            </div>
          </van-collapse-item>
        </van-collapse>

        <!-- 资质证书信息 -->
        <van-collapse v-model="activeNames" class="info-section">
          <van-collapse-item
            name="certificates"
            title="资质证书"
            :value="certificates.length"
          >
            <van-tabs v-model="certificateTab" class="certificate-tabs">
              <van-tab title="全部证书" :badge="certificates.length"></van-tab>
              <van-tab title="有效证书" :badge="validCertCount"></van-tab>
              <van-tab title="过期证书" :badge="expiredCertCount"></van-tab>
            </van-tabs>

            <div class="certificate-list">
              <van-cell
                v-for="cert in filteredCertificates"
                :key="cert.id"
                :title="cert.certificateName"
                :value="cert.certNo"
                :label="`有效期: ${formatDate(cert.startDate)} - ${formatDate(
                  cert.endDate
                )}`"
                is-link
                @click="showCertificateDetail(cert)"
                :class="{ 'expired-cert': isCertificateExpired(cert) }"
              >
                <template #right-icon>
                  <div class="status">
                    <van-tag v-if="isCertificateExpired(cert)" color="#f56c6c"
                      >已过期</van-tag
                    >
                    <van-tag v-else color="#67c23a">有效</van-tag>
                  </div>
                </template>
              </van-cell>
            </div>
          </van-collapse-item>
        </van-collapse>

        <!-- 联系方式信息 -->
        <van-collapse v-model="activeNames" class="info-section">
          <van-collapse-item name="contacts" title="联系方式">
            <div class="contact-info">
              <van-cell
                title="联系电话"
                :value="contacts.phoneNumber"
                is-link
                @click="makePhoneCall(contacts.phoneNumber)"
              ></van-cell>
              <van-cell
                v-for="(call, index) in contacts.allCalls"
                :key="index"
                :title="call.tag"
                :value="call.phoneNumber"
                :label="`来源: ${call.source}`"
                is-link
                @click="makePhoneCall(call.phoneNumber)"
              ></van-cell>
              <van-cell
                title="电子邮箱"
                :value="contacts.email"
                is-link
                @click="sendEmail(contacts.email)"
              ></van-cell>
              <van-cell
                title="注册地址"
                :value="contacts.regLocation"
              ></van-cell>
            </div>
          </van-collapse-item>
        </van-collapse>
      </div>

      <!-- 证书详情弹窗 -->
      <van-popup
        v-model="showCertDetail"
        position="bottom"
        :style="{ 'max-height': '80%' }"
        round
      >
        <div class="cert-detail">
          <div class="cert-detail-header">
            <h3>{{ currentCert.certificateName }}</h3>
            <p class="cert-no">证书编号: {{ currentCert.certNo }}</p>
            <van-tag
              :color="isCertificateExpired(currentCert) ? '#f56c6c' : '#67c23a'"
              class="cert-status"
            >
              {{ isCertificateExpired(currentCert) ? "已过期" : "有效" }}
            </van-tag>
          </div>

          <div class="cert-detail-body">
            <van-cell
              title="发证日期"
              :value="formatDate(currentCert.startDate)"
            ></van-cell>
            <van-cell
              title="到期日期"
              :value="formatDate(currentCert.endDate)"
            ></van-cell>

            <van-collapse v-model="certDetailCollapse">
              <van-collapse-item name="details" title="详细信息">
                <van-cell
                  v-for="(item, index) in currentCert.detail"
                  :key="index"
                  :title="item.title"
                  :value="item.content"
                  :label="index < currentCert.detail.length - 1 ? '' : ''"
                  class="detail-item"
                ></van-cell>
              </van-collapse-item>
            </van-collapse>
          </div>

          <van-button
            round
            block
            class="close-btn"
            @click="showCertDetail = false"
          >
            关闭
          </van-button>
        </div>
      </van-popup>

      <!-- 税务详情弹窗 -->
      <van-popup
        v-model="showTaxDetailPopup"
        position="bottom"
        :style="{ 'max-height': '80%' }"
        round
      >
        <div class="tax-detail">
          <h3>{{ currentTax.year }}年 税务信用等级详情</h3>
          <van-cell-group>
            <van-cell title="年份" :value="currentTax.year"></van-cell>
            <van-cell title="信用等级" :value="currentTax.grade"></van-cell>
            <van-cell title="类型" :value="currentTax.type"></van-cell>
            <van-cell
              title="评定部门"
              :value="currentTax.evalDepartment"
            ></van-cell>
          </van-cell-group>
          <van-button
            round
            block
            class="close-btn"
            @click="showTaxDetailPopup = false"
          >
            关闭
          </van-button>
        </div>
      </van-popup>
    </div>
  </div>
</template>

<script>
import {
  Collapse,
  CollapseItem,
  Cell,
  CellGroup,
  Empty,
  Tabs,
  Tab,
  Tag,
  Popup,
  Button,
  Icon,
  Toast,
} from "vant";
import { getXcCustomerDetail } from "@/api/xcCustomer";
export default {
  name: "EnterpriseInfo",
  components: {
    [Collapse.name]: Collapse,
    [CollapseItem.name]: CollapseItem,
    [Cell.name]: Cell,
    [CellGroup.name]: CellGroup,
    [Empty.name]: Empty,
    [Tabs.name]: Tabs,
    [Tab.name]: Tab,
    [Tag.name]: Tag,
    [Toast.name]: Toast,
    [Popup.name]: Popup,
    [Button.name]: Button,
    [Icon.name]: Icon,
  },
  created() {
    let s = this.$route.query.id;
    if (s) {
      this.noid = false;
      this.getData(s);
    } else {
      this.noid = true;
    }
  },
  data() {
    return {
      // 企业数据
      enterpriseData: {},
      // 状态管理
      activeNames: [],
      certificateTab: 0,
      showCertDetail: false,
      currentCert: {},
      showTaxDetailPopup: false,
      currentTax: {},
      certDetailCollapse: ["details"],
      noid: false,
    };
  },
  computed: {
    // 企业基本信息
    enterpriseName() {
      if (this.enterpriseData.taxCredits) {
        return this.enterpriseData.taxCredits.length > 0
          ? this.enterpriseData.taxCredits[0].name
          : "未知企业";
      }
    },
    idNumber() {
      if (this.enterpriseData.taxCredits) {
        return this.enterpriseData.taxCredits.length > 0
          ? this.enterpriseData.taxCredits[0].idNumber
          : "";
      }
    },
    regLocation() {
      if (this.enterpriseData.contacts) {
        return this.enterpriseData.contacts.regLocation || "";
      }
    },
    // 税务相关
    taxContraventions() {
      return this.enterpriseData.taxContraventions || [];
    },
    taxCredits() {
      // 按年份降序排序
      return (this.enterpriseData.taxCredits || []).sort(
        (a, b) => b.year - a.year
      );
    },
    // 证书相关
    certificates() {
      return this.enterpriseData.certificates || [];
    },
    validCertCount() {
      return this.certificates.filter(
        (cert) => !this.isCertificateExpired(cert)
      ).length;
    },
    expiredCertCount() {
      return this.certificates.filter((cert) => this.isCertificateExpired(cert))
        .length;
    },
    filteredCertificates() {
      // 根据选中的标签筛选证书
      switch (this.certificateTab) {
        case 0: // 全部
          return this.certificates;
        case 1: // 有效
          return this.certificates.filter(
            (cert) => !this.isCertificateExpired(cert)
          );
        case 2: // 过期
          return this.certificates.filter((cert) =>
            this.isCertificateExpired(cert)
          );
        default:
          return this.certificates;
      }
    },
    // 联系方式
    contacts() {
      return this.enterpriseData.contacts || {};
    },
  },
  methods: {
    getData(s) {
      Toast.loading({
        duration: 0, // 持续展示 toast
        forbidClick: true,
        message: "加载中",
      });
      getXcCustomerDetail(s)
        .then((res) => {
          this.enterpriseData = res;
          console.log("res", res);
          Toast.clear();
        })
        .catch((err) => {
          console.log("err", err);
          Toast.clear();
          this.noid = true;
        });
    },
    // 格式化日期
    formatDate(dateStr) {
      if (!dateStr) return "未知";
      // 处理类似"2025-06-12"的日期
      if (dateStr.includes("-")) {
        return dateStr;
      }
      // 处理其他格式的日期
      return dateStr;
    },
    // 判断证书是否过期
    isCertificateExpired(cert) {
      if (!cert.endDate) return true;

      // 特殊情况处理：有效期到2070年的视为有效
      if (cert.endDate.includes("2070")) return false;

      const endDate = new Date(cert.endDate);
      const today = new Date();
      return endDate < today;
    },
    // 显示证书详情
    showCertificateDetail(cert) {
      this.currentCert = cert;
      this.showCertDetail = true;
    },
    // 显示税务详情
    showTaxDetail(tax) {
      this.currentTax = tax;
      this.showTaxDetailPopup = true;
    },
    // 拨打电话
    makePhoneCall(phone) {
      // 实际项目中可以调用原生API拨打电话
      alert(`拨打: ${phone}`);
    },
    // 发送邮件
    sendEmail(email) {
      window.location.href = `mailto:${email}`;
    },
  },
};
</script>

<style scoped>
.enterprise-info {
  background-image: url("https://static.xchengtech.cn/6e22c1b6e3354e8083e41379d5d43c73.png");
  background-color: #eaf2ff;
  background-repeat: no-repeat;
  background-size: 100% auto;
  min-height: 100vh;
  padding-top: 44vw;
}

.header {
  background-color: white;
  margin: 4vw 4vw 0 4vw;
  /* color: white; */
  border-radius: 14px;
  padding: 16px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.header-content {
  max-width: 1200px;
  margin: 0 auto;
}

.enterprise-name {
  font-size: 20px;
  margin-bottom: 2vw;
  font-weight: 600;
  color: #171e42;
}

.enterprise-code {
  font-size: 14px;
  margin-bottom: 2vw;
  /* opacity: 0.9; */
  color: #72767a;
}

.enterprise-address {
  font-size: 14px;
  display: flex;
  align-items: center;
  color: #72767a;
}

.enterprise-address .van-icon {
  margin-right: 4px;
  font-size: 14px;
}

.content {
  max-width: 1200px;
  margin: 0 auto;
  padding: 16px;
}

.info-section {
  margin-bottom: 16px;
  background-color: white;
  border-radius: 14px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.info-section .van-cell,
.van-popup .van-cell {
  padding: 10px 0;
}

.info-section .van-collapse-item /deep/ .van-collapse-item__content {
  padding: 0 16px 10px 16px;
}
.info-section .van-collapse-item /deep/ .van-collapse-item__title {
  padding: 16px 16px;
  font-weight: 600;
  color: #171e42;
  font-size: 4vw;
}

.info-section /deep/ .van-info {
  right: -10px;
}

.empty-info {
  padding: 30px 16px;
  text-align: center;
}

.empty-hint {
  margin-top: 8px;
  color: #666;
  font-size: 14px;
}

.certificate-tabs {
  margin-bottom: 16px;
}

.certificate-list {
  padding-bottom: 10px;
}
.certificate-list .van-cell {
  gap: 16px;
}

.expired-cert .van-cell__title {
  color: #f56c6c;
}

.cert-detail {
  padding: 16px;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.cert-detail-header {
  /* margin-bottom: 16px; */
  position: relative;
}

.cert-detail-header h3 {
  font-size: 18px;
  font-weight: bold;
  margin-bottom: 8px;
}

.cert-no {
  color: #666;
  font-size: 14px;
  margin-bottom: 8px;
}

.cert-status {
  position: absolute;
  top: 0;
  right: 0;
}

.cert-detail-body {
  flex: 1;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}

.detail-item {
  padding: 10px 16px;
}

.detail-item .van-cell__title {
  color: #333;
  font-weight: 500;
}
.van-cell::after {
  left: 0;
  right: 0;
}
.detail-item .van-cell__value {
  color: #666;
  word-break: break-all;
}

.tax-detail {
  padding: 16px;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.tax-detail h3 {
  font-size: 18px;
  font-weight: bold;
  margin-bottom: 16px;
  text-align: center;
}

.close-btn {
  margin-top: 16px;
}
</style>



// WEBPACK FOOTER //
// src/view/myCustomer/module/companyDetail/index.vue